import i18n from 'i18next';
import jsPDF from 'jspdf';
import translations from 'src/internationalisation/translation-map.json';
import autoTable from 'jspdf-autotable';
import { drawBottomLine, transformAlignsToObject } from 'views/report/utils/add-table/utils.ts';
import { TAddTableOptions, TTableData } from 'views/report/utils/add-table/types.ts';
import { footerHeight, marginTop, paddingTopPage } from 'views/report/utils/add-table/consts.ts';
import { addTitle } from 'views/report/utils/add-title/add-title.ts';
import '../../fonts/Roobert-SemiBold-bold';
import '../../fonts/Roobert-Regular-normal';
import { blackColor, darkGrayColor, lightGrayColor, warmGrayColor } from 'views/report/colors.ts';
import { fontBold, fontMainSize, fontNormal, padding } from 'views/report/utils/style-consts.ts';
import { getCenterXPosition, getPageHeight } from 'views/report/utils/utils.ts';

export const addTable = (doc: jsPDF, tableData: TTableData | [], options: TAddTableOptions): void => {
  const {
    y,
    imageUrl,
    imageWidth = 130,
    imageHeight = 70,
    aligns,
    useTranslations = false,
    headersCount = 2,
    title,
    titleFontSize = fontMainSize,
    hasFirstColumnBoldText = true,
    hasUnitsRow = true,
  } = options;

  const head = tableData.slice(0, headersCount).map((row, i) => {
    if (i === 0 && useTranslations) {
      return row.map((cell) =>
        typeof cell === 'string'
          ? // @ts-ignore
            i18n.t(translations[cell])
          : // @ts-ignore
            i18n.t(translations[cell.content]),
      );
    }
    return row;
  });
  const body = tableData.slice(headersCount, tableData.length);

  let dataTable: any = {
    finalY: 0,
  };
  let titleCoordinates = null;

  if (title) {
    titleCoordinates = addTitle(doc, title, {
      x: padding,
      y,
      fontSize: titleFontSize,
    });
  }

  autoTable(doc, {
    didParseCell: (data) => {
      const { cell, section, column, row } = data;
      const { styles } = cell;

      if (section === 'head') {
        styles.font = fontBold.fontName;
        // @ts-ignore
        styles.fontStyle = fontBold.fontStyle;
      } else if (section === 'body' && row.index === 0 && hasUnitsRow) {
        styles.textColor = warmGrayColor;
      } else {
        styles.textColor = blackColor;
      }

      if (column.index === 0 && hasFirstColumnBoldText) {
        styles.font = fontBold.fontName;
        // @ts-ignore
        styles.fontStyle = fontBold.fontStyle;
      }
      // @ts-ignore
      styles.halign = aligns ? aligns[data.column.index] : 'left';
    },
    didDrawCell: (data) => {
      const { section } = data;

      if (section === 'head') {
        drawBottomLine(doc, data.cell, darkGrayColor);
      }

      if (section === 'body') {
        drawBottomLine(doc, data.cell, lightGrayColor);
      }
    },
    didDrawPage: (data) => {
      data.table.settings.margin.top = paddingTopPage - 5;
      dataTable = data.table;
    },
    styles: {
      font: fontNormal.fontName,
      // @ts-ignore
      fontStyle: fontNormal.fontStyle,
      fontSize: 10,
      cellPadding: { top: 2, right: 0.5, bottom: 2, left: 0 },
    },
    columnStyles: aligns && transformAlignsToObject(aligns),
    margin: {
      left: 10,
      right: 10,
      top: (titleCoordinates && titleCoordinates.y + 5) || y || 90,
      bottom: 60,
    },
    theme: 'plain',
    body,
    head,
  });
  const isNewPage = getPageHeight(doc) - dataTable.finalY - footerHeight < imageHeight;

  if (isNewPage && imageUrl) {
    doc.addPage();
  }

  if (imageUrl) {
    const imageXPosition = getCenterXPosition(doc) - imageWidth / 2;
    const imageYPosition = isNewPage ? paddingTopPage : dataTable.finalY + marginTop;

    doc.addImage(imageUrl, 'PNG', imageXPosition, imageYPosition, imageWidth, imageHeight);
  }
};
